// =====================================================================
// src/core/auth.ts
//
// Autenticación: puente entre Supabase Auth y sesionStore.
//
// Flow:
//   1. Bootstrap del app: waitForAuthInit() rehidrata sesión persistida.
//   2. Login: signIn(email, password) → valida equipo → set sesionStore.
//   3. Logout: signOut() → clear sesionStore.
//   4. Cambios externos: onAuthChange (refresh token, multi-tab) actualiza store.
//
// Política:
//   - Cada login valida que el usuario esté en tabla `equipo` Y activo.
//   - Si está en auth.users pero NO en equipo: rechazar y forzar logout.
//   - Si está suspendido (activo=false): rechazar y forzar logout.
//
// Autor: Simplificalo Consulting LLC
// =====================================================================

import { supabase, isSupabaseAuthError, getSupabaseErrorMessage } from '@/core/supabase';
import { sesionStore } from '@/core/store';
import { isDevelopment } from '@/core/env';
import { canWrite } from '@/types/domain';
import type { Role, SesionActiva } from '@/types/domain';
import type { Session, AuthChangeEvent } from '@supabase/supabase-js';

// ─── TIPOS ───────────────────────────────────────────────────────────

/** Códigos de error que auth.ts puede retornar. Tipados para UI. */
export type AuthErrorCode =
  | 'invalid_credentials'
  | 'email_not_confirmed'
  | 'user_not_in_team'
  | 'user_inactive'
  | 'rate_limited'
  | 'network_error'
  | 'unknown';

/** Resultado discriminado de un intento de login. */
export type SignInResult =
  | { ok: true; sesion: SesionActiva }
  | { ok: false; code: AuthErrorCode; message: string };

/** Eventos de auth de alto nivel (filtrados desde Supabase). */
export type AuthEvent = 'signed_in' | 'signed_out' | 'session_refreshed';

// ─── ESTADO INTERNO ──────────────────────────────────────────────────

/**
 * Promise que resuelve cuando la hidratación inicial completa.
 * El bootstrap del app debe await esto antes de decidir UI.
 */
let authInitResolve: (() => void) | null = null;
const authInitPromise = new Promise<void>((resolve) => {
  authInitResolve = resolve;
});
let authInitialized = false;

// ─── HELPERS PRIVADOS ────────────────────────────────────────────────

/**
 * Construye SesionActiva a partir de la sesión cruda de Supabase.
 * Hace query a tabla `equipo` para enriquecer con rol/activo/nombre.
 *
 * Retorna null si:
 *   - El user no está en tabla equipo (bug trigger).
 *   - El user está suspendido (activo=false).
 *   - Hay error en la query.
 */
const buildSesionFromAuth = async (
  session: Session,
): Promise<{ ok: true; sesion: SesionActiva } | { ok: false; code: AuthErrorCode }> => {
  const userId = session.user.id;
  const email = session.user.email;

  if (typeof email !== 'string' || email.length === 0) {
    return { ok: false, code: 'unknown' };
  }

  const { data, error } = await supabase
    .from('equipo')
    .select('id, email, nombre, rol, activo')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    if (isDevelopment()) {
      console.error('[auth] Error consultando equipo:', error);
    }
    return { ok: false, code: 'unknown' };
  }

  if (data === null) {
    if (isDevelopment()) {
      console.error(`[auth] User ${userId} en auth pero no en equipo`);
    }
    return { ok: false, code: 'user_not_in_team' };
  }

  if (!data.activo) {
    if (isDevelopment()) {
      console.warn(`[auth] User ${userId} suspendido (activo=false)`);
    }
    return { ok: false, code: 'user_inactive' };
  }

  return {
    ok: true,
    sesion: {
      userId: data.id,
      email: data.email,
      nombre: data.nombre,
      rol: data.rol,
      activo: data.activo,
    },
  };
};

/**
 * Mapea errores crudos de Supabase Auth a AuthErrorCode tipado.
 */
const mapSupabaseAuthErrorToCode = (error: unknown): AuthErrorCode => {
  if (!isSupabaseAuthError(error)) return 'network_error';

  const msg = error.message.toLowerCase();
  if (msg.includes('invalid login credentials')) return 'invalid_credentials';
  if (msg.includes('email not confirmed')) return 'email_not_confirmed';
  if (msg.includes('rate limit') || msg.includes('too many')) return 'rate_limited';
  return 'unknown';
};

/**
 * Mensaje legible en español (Colombia, tuteo neutro) para cada AuthErrorCode.
 */
const getMessageForCode = (code: AuthErrorCode): string => {
  switch (code) {
    case 'invalid_credentials':
      return 'Email o contraseña incorrectos.';
    case 'email_not_confirmed':
      return 'Tu email aún no está confirmado. Revisa tu bandeja de entrada.';
    case 'user_not_in_team':
      return 'Tu cuenta no tiene acceso al panel. Contacta al administrador.';
    case 'user_inactive':
      return 'Tu cuenta está suspendida. Contacta al administrador.';
    case 'rate_limited':
      return 'Demasiados intentos. Intenta de nuevo en unos minutos.';
    case 'network_error':
      return 'Error de conexión. Verifica tu internet.';
    case 'unknown':
      return 'Error desconocido. Intenta de nuevo.';
  }
};

// ─── API PÚBLICA ─────────────────────────────────────────────────────

/**
 * Login con email + password.
 *
 * @example
 *   const result = await signIn('rebeca@example.com', 'mi-password');
 *   if (!result.ok) {
 *     showToast(result.message, 'error');
 *     return;
 *   }
 *   redirectTo('/dashboard');
 */
export const signIn = async (email: string, password: string): Promise<SignInResult> => {
  const trimmedEmail = email.trim().toLowerCase();
  if (trimmedEmail.length === 0 || password.length === 0) {
    return {
      ok: false,
      code: 'invalid_credentials',
      message: getMessageForCode('invalid_credentials'),
    };
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email: trimmedEmail,
    password,
  });

  if (error) {
    const code = mapSupabaseAuthErrorToCode(error);
    return { ok: false, code, message: getMessageForCode(code) };
  }

  // Sesión auth OK. Validar equipo + activo.
  const sesionResult = await buildSesionFromAuth(data.session);
  if (!sesionResult.ok) {
    // No es miembro válido: forzar logout para limpiar sesión.
    await supabase.auth.signOut();
    return {
      ok: false,
      code: sesionResult.code,
      message: getMessageForCode(sesionResult.code),
    };
  }

  sesionStore.set(sesionResult.sesion);
  return { ok: true, sesion: sesionResult.sesion };
};

/**
 * Logout. Limpia sesión local y revoca el JWT en Supabase.
 *
 * @example
 *   await signOut();
 *   redirectTo('/login');
 */
export const signOut = async (): Promise<void> => {
  const { error } = await supabase.auth.signOut();
  if (error && isDevelopment()) {
    console.warn('[auth] Error en signOut:', getSupabaseErrorMessage(error));
  }
  sesionStore.set(null);
};

/**
 * Lanza redirect si no hay sesión activa o el rol requerido no coincide.
 *
 * @param requiredRole Rol mínimo requerido. Si undefined, solo verifica
 *                     que haya sesión.
 *
 * @example
 *   const sesion = requireAuth('admin');
 *   if (!sesion) return;
 */
export const requireAuth = (requiredRole?: Role): SesionActiva | null => {
  const sesion = sesionStore.get();

  if (sesion === null) {
    redirectToLogin();
    return null;
  }

  if (!sesion.activo) {
    redirectToLogin();
    return null;
  }

  if (requiredRole === 'admin' && sesion.rol !== 'admin') {
    redirectToUnauthorized();
    return null;
  }

  if (requiredRole === 'editor' && !canWrite(sesion.rol)) {
    redirectToUnauthorized();
    return null;
  }

  return sesion;
};

/**
 * Redirige al login. Centralizado para que cualquier cambio de ruta
 * sea un solo lugar.
 */
export const redirectToLogin = (): void => {
  if (typeof window !== 'undefined') {
    window.location.href = '/index.html';
  }
};

/**
 * Redirige a página de "no autorizado". Por ahora también es login,
 * pero podría ser una página dedicada en el futuro.
 */
export const redirectToUnauthorized = (): void => {
  if (typeof window !== 'undefined') {
    window.location.href = '/index.html?error=unauthorized';
  }
};

/**
 * Suscribirse a cambios de auth (login, logout, refresh).
 * Wrapper sobre supabase.auth.onAuthStateChange con eventos tipados.
 *
 * @example
 *   const cleanup = onAuthChange((event, sesion) => {
 *     if (event === 'signed_out') redirectToLogin();
 *   });
 */
export const onAuthChange = (
  callback: (event: AuthEvent, sesion: SesionActiva | null) => void,
): (() => void) => {
  const handler = (rawEvent: AuthChangeEvent, _session: Session | null): void => {
    const event = mapSupabaseEventToAuthEvent(rawEvent);
    if (event === null) return;
    callback(event, sesionStore.get());
  };

  const { data } = supabase.auth.onAuthStateChange(handler);
  return (): void => {
    data.subscription.unsubscribe();
  };
};

/**
 * Mapea eventos crudos de Supabase a nuestros eventos de negocio.
 * Filtra eventos irrelevantes retornando null.
 */
const mapSupabaseEventToAuthEvent = (event: AuthChangeEvent): AuthEvent | null => {
  switch (event) {
    case 'SIGNED_IN':
      return 'signed_in';
    case 'SIGNED_OUT':
      return 'signed_out';
    case 'TOKEN_REFRESHED':
      return 'session_refreshed';
    case 'INITIAL_SESSION':
    case 'USER_UPDATED':
    case 'PASSWORD_RECOVERY':
    case 'MFA_CHALLENGE_VERIFIED':
      return null;
    default:
      return null;
  }
};

/**
 * Espera a que la hidratación inicial complete.
 * El bootstrap del app debe await esto antes de decidir UI inicial.
 *
 * @example
 *   await waitForAuthInit();
 *   if (sesionStore.get() === null) showLogin();
 *   else showDashboard();
 */
export const waitForAuthInit = async (): Promise<void> => {
  if (authInitialized) return;
  await authInitPromise;
};

// ─── HIDRATACIÓN INICIAL (al importar el módulo) ─────────────────────

/**
 * Al cargar este módulo, intentamos rehidratar la sesión.
 * Si Supabase tiene una sesión persistida en localStorage, la traemos
 * y enriquecemos con datos de equipo.
 */
const hydrateSession = async (): Promise<void> => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      if (isDevelopment()) {
        console.warn('[auth] Error rehidratando sesión:', getSupabaseErrorMessage(error));
      }
      return;
    }

    if (data.session === null) {
      // No hay sesión persistida. OK, el usuario no está logged in.
      return;
    }

    const result = await buildSesionFromAuth(data.session);
    if (result.ok) {
      sesionStore.set(result.sesion);
    } else {
      // Sesión existe en Supabase pero el miembro no es válido (no está
      // en equipo o está suspendido). Limpiar.
      await supabase.auth.signOut();
    }
  } finally {
    authInitialized = true;
    authInitResolve?.();
  }
};

// Ejecutar hidratación inmediatamente al importar.
// El bootstrap del app puede await waitForAuthInit() para esperar.
void hydrateSession();
